'use client';

import { useState } from 'react';
import { FiCheckCircle, FiShield } from 'react-icons/fi';
import Image from 'next/image';

const credentials = [
  {
    title: 'Project Management Professional (PMP)®',
    institution: 'Project Management Institute (PMI)',
    logo: '/images/certifications/pmp.png',
    description: 'Expertise in strategic planning, risk mitigation, and cross-functional leadership across complex project lifecycles.',
    featured: true
  },
  {
    title: 'Certified Scrum Master (CSM)®',
    institution: 'Scrum Alliance',
    logo: '/images/certifications/csm.png',
    description: 'Advanced proficiency in Agile frameworks, facilitating team collaboration and iterative product delivery.',
    featured: true
  },
  {
    title: 'Deep Learning Specialization',
    institution: 'DeepLearning.AI',
    logo: '/images/certifications/deep-learning.png',
    description: 'Building and optimizing neural networks, CNNs, and RNNs for computer vision and NLP applications.',
    featured: true
  },
  {
    title: 'Certified Python Professional (PCPP)',
    institution: 'Python Institute',
    logo: '/images/certifications/python.png',
    description: 'Advanced proficiency in OOP, network communication, and high-performance application development.',
    featured: true
  }
];

const smallCerts = [
  { name: 'Google Advanced Data Analytics', issuer: 'Google' },
  { name: 'IBM Data Science', issuer: 'IBM' },
  { name: 'Leadership & Management', issuer: 'US Coast Guard' },
  { name: 'TensorFlow Advanced Techniques', issuer: 'Coursera' },
  { name: 'Machine Learning Engineering', issuer: 'Coursera' },
];

export default function AwardsSection() {
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});

  const toggleExpand = (idx: number) => {
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  return (
    <section className="py-24">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2 text-indigo-600">
            <FiShield className="animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em]">Credential Verification</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter uppercase leading-[0.9]">
            Professional <br /><span className="text-zinc-400">Licensing.</span>
          </h2>
        </div>
      </div>

      {/* Featured Certs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
        {credentials.map((cert, idx) => {
          const isExpanded = expanded[idx];

          return (
            <div
              key={idx}
              onClick={() => toggleExpand(idx)}
              className={`group relative p-8 rounded-[2.5rem] border transition-all duration-500 overflow-hidden cursor-pointer
                          bg-white dark:bg-zinc-900
                          ${isExpanded ? 'border-indigo-500/50' : 'border-zinc-100 dark:border-zinc-800'}
                          hover:border-indigo-500/50`}
            >
              <div className="flex items-start gap-6">
                <div
                  className={`relative w-16 h-16 shrink-0 transition-all duration-500
                              ${isExpanded ? 'grayscale-0' : 'grayscale group-hover:grayscale-0'}`}
                >
                  <Image src={cert.logo} alt={cert.title} fill className="object-contain" />
                </div>
                <div className="space-y-2">
                  <h3
                    className={`text-xl font-black tracking-tight leading-tight transition-colors
                                ${isExpanded ? 'text-indigo-600' : 'group-hover:text-indigo-600'}`}
                  >
                    {cert.title}
                  </h3>
                  <p className="text-[10px] font-black uppercase tracking-widest text-zinc-400">
                    Issued by {cert.institution}
                  </p>
                  <p
                    className={`text-sm text-zinc-500 dark:text-zinc-400 leading-relaxed transition-all duration-300
                                ${isExpanded ? 'line-clamp-none' : 'line-clamp-2'}`}
                  >
                    {cert.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Secondary Certs Bar */}
      <div className="bg-zinc-50 dark:bg-zinc-900/50 rounded-[2rem] p-8 border border-zinc-100 dark:border-zinc-800">
        <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-400 mb-8 text-center">
          Extended Technical Certifications
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-6 gap-x-12">
          {smallCerts.map((cert, idx) => (
            <div
              key={idx}
              className="flex items-center gap-3 group active:text-indigo-600 cursor-pointer"
            >
              <FiCheckCircle className="text-indigo-500 shrink-0" />
              <div>
                <p className="text-xs font-bold text-zinc-900 dark:text-white uppercase tracking-tight group-hover:text-indigo-600 transition-colors">
                  {cert.name}
                </p>
                <p className="text-[9px] font-medium text-zinc-500 uppercase tracking-widest">
                  {cert.issuer}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
